import { useEffect, useState, useCallback } from "react";
import useColors from "../../theme/useColors";
import { Box, TextInput, Skeleton } from "@mantine/core";
import { CustomSelect } from "../Shared/customSelect";
import { IconSearch, IconX } from "@tabler/icons-react";
import PollingRefreshIndicator from "../Shared/PollingRefreshIndicator";

interface FilterComponentProps {
  dropdown1Values: string[];
  dropDown1Options: any[];
  dropDown2Options: any[];
  dropDown3Options?: any[];
  setDropdown1Values: (values: string[]) => void;
  dropdown2Values: string[];
  setDropdown2Values: (values: string[]) => void;
  dropdown3Values?: string[];
  setDropdown3Values?: (values: string[]) => void;
  searchValue: string;
  setSearchValue: (values: string) => void;
  hideThirdDropdown?: boolean;
  hideSecondDropdown?: boolean;
  hideFirstDropdown?: boolean;
  loading?: boolean;
  refreshing?: boolean;
  debounceDelay?: number;
  dropdown1PlaceHolder?: string;
}

const FilterComponent: React.FC<FilterComponentProps> = ({
  dropdown1Values,
  dropDown1Options,
  dropDown2Options,
  dropDown3Options,
  setDropdown1Values,
  dropdown2Values,
  setDropdown2Values,
  dropdown3Values,
  setDropdown3Values,
  searchValue,
  setSearchValue,
  hideThirdDropdown = false,
  hideSecondDropdown = false,
  hideFirstDropdown = false,
  loading = false,
  refreshing = false,
  debounceDelay = 300,
  dropdown1PlaceHolder,
}) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [internalSearchValue, setInternalSearchValue] = useState(searchValue);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValue(internalSearchValue);
    }, debounceDelay);

    return () => {
      clearTimeout(timer);
    };
  }, [internalSearchValue, debounceDelay, setSearchValue]);

  // Sync internal value with external value when it changes from outside
  useEffect(() => {
    setInternalSearchValue(searchValue);
  }, [searchValue]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Determine screen size breakpoints
  const isExtraSmall = windowWidth <= 569;
  const isMobile = windowWidth < 568;
  const isSmallerMobile = windowWidth<449
  const isTablet = windowWidth >= 668 && windowWidth < 1024;

  // Get responsive search width
  const getSearchWidth = () => {
    if (isSmallerMobile) return "100%"
    if (isMobile) return "180px";
    if (isTablet) return "330px";
    return "380px";
  };

  // Get responsive icon size
  const getIconSize = () => {
    if (isMobile) return 16;
    if (isTablet) return 17;
    return 18;
  };

  // Get responsive component size
  const getComponentSize = () => {
    return isExtraSmall ? "xs" : isMobile ? "sm" : "sm";
  };
  const getComponentSizeForSelect = () => {
    return isExtraSmall ? "xs" : isMobile ? "md" : "md";
  };


  // Function to clear the search input
  const clearSearch = useCallback(() => {
    setInternalSearchValue("");
    setSearchValue("");
  }, [setSearchValue]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInternalSearchValue(e.target.value);
    },
    []
  );

  const colors = useColors();

  // Render skeleton loader when loading
  if (loading) {
    return (
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: isMobile ? "16px" : "8px",
        }}
        pt={"4px"}
      >
        <Box
          pb={isMobile ? "0" : "4px"}
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: "8px",
            width: isMobile ? "100%" : "auto",
            height: "100%",
            overflow: "hidden",
          }}
        >
          {!hideFirstDropdown && (
            <Skeleton height={36} radius="md" width={isMobile ? "100%" : 200} />
          )}
          {!hideSecondDropdown && (
            <Skeleton height={36} radius="md" width={isMobile ? "100%" : 200} />
          )}
          {!hideThirdDropdown && (
            <Skeleton height={36} radius="md" width={isMobile ? "100%" : 200} />
          )}
        </Box>

        <Box style={{ width: isMobile ? "100%" : "auto" }}>
          <TextInput
            miw={getSearchWidth()}
            size={getComponentSize()}
            radius={"md"}
            mb={isMobile ? "10px" : ""}
            w={"100%"}
            placeholder="Search by query ID or Patient Name or MRN"
            value={internalSearchValue}
            onChange={handleSearchChange}
            leftSection={
              <IconSearch size={getIconSize()} color={colors.primary[5]} />
            }
            rightSection={
              internalSearchValue ? (
                <IconX
                  size={getIconSize() - 2}
                  color={colors.gray[6]}
                  style={{ cursor: "pointer" }}
                  onClick={clearSearch}
                />
              ) : null
            }
            rightSectionPointerEvents="auto"
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: isMobile ? "8px" : "8px",
      }}
      pt={"4px"}
    >
      <Box
        pb={"4px"}
        style={{
          display: "flex",
          gap: "8px",
          width: isMobile ? "100%" : "auto",
          height: "100%",
        }}
      >
        {!hideFirstDropdown && (
          <CustomSelect
            options={dropDown1Options}
            value={dropdown1Values}
            onChange={setDropdown1Values}
            placeholder={
              dropdown1PlaceHolder ? dropdown1PlaceHolder : "Select Status"
            }
            searchable={true}
            searchPlaceholder="Search options..."
            size={getComponentSizeForSelect()}
          />
        )}
        {!hideSecondDropdown && (
          <CustomSelect
            options={dropDown2Options}
            value={dropdown2Values}
            onChange={setDropdown2Values}
            placeholder="Select Topic"
            searchable={true}
            searchPlaceholder="Search options..."
            size={getComponentSizeForSelect()}
          />
        )}
        {!hideThirdDropdown &&
          dropDown3Options &&
          dropdown3Values &&
          setDropdown3Values && (
            <CustomSelect
              options={dropDown3Options}
              value={dropdown3Values}
              onChange={setDropdown3Values}
              placeholder="Select Time"
              size={getComponentSizeForSelect()}
            />
          )}
      </Box>
      <Box
        style={{
          width: isMobile ? "auto" : "auto",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <PollingRefreshIndicator visible={refreshing} />
        <TextInput
          miw={getSearchWidth()}
          size={getComponentSize()}
          radius={"md"}
          mb={"4px"}
          w={"100%"}
          placeholder="Search by query ID or Patient Name or MRN"
          value={internalSearchValue}
          onChange={handleSearchChange}
          leftSection={
            <IconSearch size={getIconSize()} color={colors.primary[5]} />
          }
          rightSection={
            internalSearchValue ? (
              <IconX
                size={getIconSize() - 2}
                color={colors.gray[6]}
                style={{ cursor: "pointer" }}
                onClick={clearSearch}
              />
            ) : null
          }
          rightSectionPointerEvents="auto"
        />
      </Box>
    </Box>
  );
};

export default FilterComponent;